import { useEffect, useRef, useState } from 'react';
import type {
  ObligationDTO,
  ObligationKind,
  ObligationPriority,
  ObligationVisibility,
  OccasionType,
} from '@almanac/shared';
import {
  findExistingOccasion,
  joinPersonNames,
  liveObligationTitle,
  useDeleteObligation,
  useLifeAreas,
  useObligations,
  useUpdateObligation,
  type RecurrenceEnd,
  splitRecurrenceEnd,
  withRecurrenceEnd,
} from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';
import { PersonCard } from './PersonCard.js';
import { PersonPicker } from './PersonPicker.js';
import { ReminderPrompt } from './ReminderPrompt.js';

function yearlyRuleFromDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `FREQ=YEARLY;BYMONTH=${d.getMonth() + 1};BYMONTHDAY=${d.getDate()}`;
}

function yearFromIso(iso: string): number {
  return Number(iso.slice(0, 4));
}

function formatNoticeDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

interface Props {
  obligation: ObligationDTO | null;
  onClose: () => void;
}

type RecurrenceMode = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

interface DraftState {
  title: string;
  description: string;
  lifeAreaId: string;
  dueDate: string;
  /** Optional `HH:MM`. Null/empty means all-day. Always null for
   *  occasions — birthdays are full-day events. */
  dueTime: string;
  recurrenceMode: RecurrenceMode;
  recurrenceCustom: string;
  /** End condition for the standard cadences; custom rules carry any
   *  COUNT/UNTIL inside the raw string instead. */
  recurrenceEnd: RecurrenceEnd;
  priority: ObligationPriority;
  notes: string;
  kind: ObligationKind;
  // Owner-private by default; 'shared' opens the item to the other
  // household member(s). Enforced server-side (see obligations route).
  visibility: ObligationVisibility;
  occasionType: OccasionType;
  occasionLabel: string;
  // Anniversaries can have multiple subjects (wedding); birthdays
  // have one. Both modes work off the same array so the UI can flip
  // shape without re-shaping the draft.
  personIds: string[];
}

/**
 * The wire format is an iCal RRULE; the form offers a friendlier set
 * of presets and falls back to a raw RRULE textbox for power users.
 *
 * Round-trip rule: a pure `FREQ=DAILY`/`WEEKLY`/`MONTHLY`/`YEARLY` rule
 * maps back to the corresponding preset. Anything else (intervals,
 * BYMONTH, BYDAY, etc.) opens as Custom with the raw rule prefilled,
 * so editing a complex rule keeps it intact.
 */
function parseRecurrence(rule: string | null | undefined): {
  mode: RecurrenceMode;
  raw: string;
  end: RecurrenceEnd;
} {
  if (!rule || !rule.trim()) return { mode: 'none', raw: '', end: { kind: 'never' } };
  const trimmed = rule.trim().replace(/^RRULE:/u, '');
  // COUNT/UNTIL split off first so "FREQ=WEEKLY;COUNT=10" still reads
  // as the Weekly cadence with an end, not as a custom rule.
  const { base, end } = splitRecurrenceEnd(trimmed);
  if (base === 'FREQ=DAILY') return { mode: 'daily', raw: '', end };
  if (base === 'FREQ=WEEKLY') return { mode: 'weekly', raw: '', end };
  if (base === 'FREQ=MONTHLY') return { mode: 'monthly', raw: '', end };
  if (base === 'FREQ=YEARLY') return { mode: 'yearly', raw: '', end };
  // Custom rules keep the full string (end parts included) — the raw
  // field owns the whole rule for power users.
  return { mode: 'custom', raw: trimmed, end: { kind: 'never' } };
}

function buildRecurrence(mode: RecurrenceMode, raw: string, end: RecurrenceEnd): string | null {
  switch (mode) {
    case 'none':
      return null;
    case 'daily':
      return withRecurrenceEnd('FREQ=DAILY', end);
    case 'weekly':
      return withRecurrenceEnd('FREQ=WEEKLY', end);
    case 'monthly':
      return withRecurrenceEnd('FREQ=MONTHLY', end);
    case 'yearly':
      return withRecurrenceEnd('FREQ=YEARLY', end);
    case 'custom': {
      const trimmed = raw.trim();
      return trimmed ? trimmed : null;
    }
  }
}

function toDraft(o: ObligationDTO): DraftState {
  const recurrence = parseRecurrence(o.recurrenceRule);
  return {
    title: o.title,
    description: o.description ?? '',
    lifeAreaId: o.lifeAreaId,
    dueDate: (o.dueDate ?? '').slice(0, 10),
    dueTime: o.dueTime ?? '',
    recurrenceMode: recurrence.mode,
    recurrenceCustom: recurrence.raw,
    recurrenceEnd: recurrence.end,
    priority: o.priority,
    notes: o.notes ?? '',
    kind: o.kind,
    visibility: o.visibility,
    occasionType: o.occasionType ?? 'birthday',
    occasionLabel: o.occasionLabel ?? '',
    personIds: o.personIds,
  };
}

// Drag-to-dismiss thresholds. Released past `DRAG_DISMISS_PX` →
// animate off-screen and close. Anything less → spring back. The
// CSS transition tied to `.drawer` (in styles.css) handles both
// movements; we only set the offset and let CSS animate it.
const DRAG_DISMISS_PX = 200;
const DRAG_CLOSE_ANIMATION_MS = 220;

export function EditDrawer({ obligation, onClose }: Props) {
  const lifeAreas = useLifeAreas();
  const obligationsList = useObligations();
  const people = usePeople();
  const update = useUpdateObligation();
  const del = useDeleteObligation();
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // A person opened from the "View person" link — the shared read-first
  // card, stacked above this drawer.
  const [viewingPersonId, setViewingPersonId] = useState<string | null>(null);
  // Pixel offset applied to the drawer's transform while the user
  // drags it down by the grab handle. `dragging` flips off the
  // transform transition so the sheet tracks the finger 1:1; on
  // release the transition fades back in for the spring-back or
  // dismiss animation.
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef<number | null>(null);

  useEffect(() => {
    setDraft(obligation ? toDraft(obligation) : null);
    setConfirmingDelete(false);
    setViewingPersonId(null);
    setDragOffset(0);
    setDragging(false);
    dragStartY.current = null;
  }, [obligation]);

  useEffect(() => {
    if (!obligation) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (confirmingDelete) {
        setConfirmingDelete(false);
      } else {
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [obligation, onClose, confirmingDelete]);

  // Hard scroll lock for the page underneath the drawer. The drag
  // handle gives drags on the sheet a productive purpose (dismiss),
  // so locking the background fully is now the right trade — drags
  // never leak to the list below in PWA mode.
  useEffect(() => {
    if (!obligation) return;
    document.body.classList.add('body--drawer-open');
    function blockTouchMove(e: TouchEvent) {
      const target = e.target as Node | null;
      const drawerBody = document.querySelector('.drawer__body');
      if (drawerBody && target && drawerBody.contains(target)) return;
      e.preventDefault();
    }
    document.addEventListener('touchmove', blockTouchMove, { passive: false });
    return () => {
      document.body.classList.remove('body--drawer-open');
      document.removeEventListener('touchmove', blockTouchMove);
    };
  }, [obligation]);

  function handleDragStart(e: React.TouchEvent<HTMLDivElement>) {
    if (e.touches.length !== 1) return;
    // Don't start a drag from a button/input or anything inside the
    // scrollable body: a tap on Close shouldn't morph into a dismiss
    // gesture, and body drags belong to the body's own scroll.
    const target = e.target as HTMLElement;
    if (target.closest('button, input, textarea, select, label')) return;
    if (target.closest('.drawer__body')) return;
    dragStartY.current = e.touches[0]!.clientY;
    setDragging(true);
  }

  function handleDragMove(e: React.TouchEvent<HTMLDivElement>) {
    if (dragStartY.current === null) return;
    const delta = e.touches[0]!.clientY - dragStartY.current;
    // Clamp to non-negative so dragging up does nothing — the sheet
    // can't go past its resting position.
    setDragOffset(Math.max(0, delta));
  }

  function handleDragEnd() {
    if (dragStartY.current === null) return;
    dragStartY.current = null;
    setDragging(false);
    if (dragOffset > DRAG_DISMISS_PX) {
      // Animate fully off-screen, then unmount. window.innerHeight is
      // a generous overshoot — the transition runs for the same
      // duration regardless of how far the offset moves.
      setDragOffset(window.innerHeight);
      window.setTimeout(() => {
        onClose();
        setDragOffset(0);
      }, DRAG_CLOSE_ANIMATION_MS);
    } else {
      setDragOffset(0);
    }
  }

  if (!obligation || !draft) return null;

  // Duplicate guard only applies to birthdays (one per person). An
  // anniversary can legitimately have several per person — wedding,
  // first-date, arrival — so we don't second-guess the user there.
  const duplicate =
    draft.kind === 'occasion' && draft.occasionType === 'birthday'
      ? findExistingOccasion(
          obligationsList.data,
          draft.personIds[0] ?? null,
          'birthday',
          obligation.id,
        )
      : null;
  const saveDisabled =
    duplicate !== null || (draft.kind === 'occasion' && draft.personIds.length === 0);

  function save() {
    if (!obligation || !draft) return;
    if (draft.kind === 'occasion') {
      const names = draft.personIds
        .map((id) => people.data?.find((p) => p.id === id)?.displayName)
        .filter((n): n is string => Boolean(n));
      update.mutate({
        id: obligation.id,
        input: {
          title: joinPersonNames(names) || obligation.title,
          description: draft.description || null,
          lifeAreaId: draft.lifeAreaId,
          dueDate: draft.dueDate,
          recurrenceRule: yearlyRuleFromDate(draft.dueDate),
          notes: draft.notes || null,
          kind: 'occasion',
          occasionType: draft.occasionType,
          occasionLabel:
            draft.occasionType === 'anniversary'
              ? draft.occasionLabel.trim() || null
              : null,
          anchorYear: yearFromIso(draft.dueDate),
          personIds: draft.personIds,
          visibility: draft.visibility,
        },
      });
    } else {
      update.mutate({
        id: obligation.id,
        input: {
          title: draft.title,
          description: draft.description || null,
          lifeAreaId: draft.lifeAreaId,
          dueDate: draft.dueDate,
          dueTime: draft.dueTime || null,
          recurrenceRule: buildRecurrence(draft.recurrenceMode, draft.recurrenceCustom, draft.recurrenceEnd),
          priority: draft.priority,
          notes: draft.notes || null,
          visibility: draft.visibility,
        },
      });
    }
    onClose();
  }

  function remove() {
    if (!obligation) return;
    del.mutate({ id: obligation.id });
    onClose();
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden />
      <aside
        className={`drawer ${dragging ? 'drawer--dragging' : ''}`}
        style={{ ['--drawer-drag-y' as string]: `${dragOffset}px` }}
        role="dialog"
        aria-label="Edit obligation"
      >
        <div
          className="drawer__grip"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onTouchCancel={handleDragEnd}
        >
          <div className="drawer__handle" aria-hidden />
          <header className="drawer__header">
            <h2 className="drawer__title">Edit obligation</h2>
            <button type="button" className="btn btn--ghost btn--small" onClick={onClose}>
              Close
            </button>
          </header>
        </div>

        <div className="drawer__body">
          {/* Kind is fixed once an entry exists — converting between
              task and occasion is more confusing than useful, and a
              delete-and-recreate is the right model when something
              was miscategorised. No kind toggle here. */}

          {draft.kind === 'occasion' ? (
            <>
              <Field label={draft.occasionType === 'anniversary' ? 'People' : 'Person'}>
                {draft.occasionType === 'anniversary' ? (
                  // Anniversaries are commonly between two subjects (a
                  // wedding), occasionally more. Multi-select picker.
                  <PersonPicker
                    multi
                    values={draft.personIds}
                    onValuesChange={(ids) => setDraft({ ...draft, personIds: ids })}
                  />
                ) : (
                  <PersonPicker
                    value={draft.personIds[0] ?? null}
                    onChange={(id) => setDraft({ ...draft, personIds: id ? [id] : [] })}
                  />
                )}
                {/* Each selected person gets a "View person" link that
                    opens the shared read-first person card (stacked
                    above this drawer) — the one door to person details
                    everywhere in the app. One person → a generic label;
                    several (an anniversary) → one link per first name. */}
                {draft.personIds.length === 1 && (
                  <button
                    type="button"
                    className="field__inline-action"
                    onClick={() => setViewingPersonId(draft.personIds[0]!)}
                  >
                    View person ›
                  </button>
                )}
                {draft.personIds.length > 1 &&
                  draft.personIds.map((pid) => {
                    const name = people.data?.find((p) => p.id === pid)?.displayName;
                    if (!name) return null;
                    return (
                      <button
                        key={pid}
                        type="button"
                        className="field__inline-action"
                        onClick={() => setViewingPersonId(pid)}
                      >
                        View {name.trim().split(/\s+/u)[0]} ›
                      </button>
                    );
                  })}
              </Field>

              {duplicate && (
                <div className="drawer__notice" role="status">
                  This person already has a birthday on{' '}
                  {formatNoticeDate(duplicate.dueDate)}. Switch back to the previous
                  person, or open the existing entry instead.
                </div>
              )}

              <div className="field-row">
                <Field label="Occasion">
                  <select
                    className="field__input field__input--select"
                    name="occasion-type"
                    value={draft.occasionType}
                    onChange={(e) =>
                      setDraft({ ...draft, occasionType: e.target.value as OccasionType })
                    }
                  >
                    <option value="birthday">Birthday</option>
                    <option value="anniversary">Anniversary</option>
                  </select>
                </Field>
                <Field
                  label={
                    draft.occasionType === 'birthday' ? 'Birth date' : 'Original date'
                  }
                  hint="Used to compute the next occurrence and the age / years."
                >
                  <input
                    className="field__input"
                    type="date"
                    name="due-date"
                    value={draft.dueDate}
                    onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                  />
                </Field>
              </div>

              {/* Free-text 'what is it' for anniversaries. Surfaces
                  on the list card as 'Wedding · 25 years'. Hidden for
                  birthdays — they're already self-describing. */}
              {draft.occasionType === 'anniversary' && (
                <Field
                  label="What is it?"
                  hint="Wedding, partnership, first met, started a job — whatever this anniversary marks."
                >
                  <input
                    className="field__input"
                    type="text"
                    name="occasion-label"
                    value={draft.occasionLabel}
                    placeholder="Wedding, partnership, met…"
                    onChange={(e) => setDraft({ ...draft, occasionLabel: e.target.value })}
                  />
                </Field>
              )}
            </>
          ) : (
            <>
              <Field label="Title">
                <input
                  className="field__input"
                  type="text"
                  name="title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
              </Field>

              <div className="field-row">
                <Field label="Due date">
                  <input
                    className="field__input"
                    type="date"
                    name="due-date"
                    value={draft.dueDate}
                    onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                  />
                </Field>
                <Field
                  label="Time"
                  hint="Optional. Leave blank for an all-day item."
                >
                  <input
                    className="field__input"
                    type="time"
                    name="due-time"
                    value={draft.dueTime}
                    onChange={(e) => setDraft({ ...draft, dueTime: e.target.value })}
                  />
                </Field>
              </div>
              {/* Surface reminders inline once the user has pinned
                  a time — the prompt self-hides when reminders
                  are already on or the user has dismissed. */}
              {draft.dueTime && <ReminderPrompt />}

              <Field label="Priority">
                <select
                  className="field__input field__input--select"
                  name="priority"
                  value={draft.priority}
                  onChange={(e) =>
                    setDraft({ ...draft, priority: e.target.value as ObligationPriority })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </Field>
            </>
          )}

          {/* Life area is meaningful for tasks (Documents, Household,
              Health, …) but adds noise for occasions, which are
              uniformly personal. Hidden in occasion mode; the stored
              value remains whatever it was previously, defaulting to
              Personal on creation. */}
          {draft.kind === 'task' && (
            <Field label="Life area">
              <select
                className="field__input field__input--select"
                name="life-area"
                value={draft.lifeAreaId}
                onChange={(e) => setDraft({ ...draft, lifeAreaId: e.target.value })}
              >
                {lifeAreas.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {draft.kind === 'task' && (
            <>
              <Field label="Repeats">
                <select
                  className="field__input field__input--select"
                  name="repeats"
                  value={draft.recurrenceMode}
                  onChange={(e) =>
                    setDraft({ ...draft, recurrenceMode: e.target.value as RecurrenceMode })
                  }
                >
                  <option value="none">Doesn&rsquo;t repeat</option>
                  <option value="daily">Every day</option>
                  <option value="weekly">Every week</option>
                  <option value="monthly">Every month</option>
                  <option value="yearly">Every year</option>
                  <option value="custom">Custom&hellip;</option>
                </select>
              </Field>

              {draft.recurrenceMode === 'custom' && (
                <Field
                  label="Custom recurrence rule"
                  hint="iCal RRULE syntax. Example: FREQ=YEARLY;BYMONTH=3;BYMONTHDAY=15 (yearly on 15 March)."
                >
                  <input
                    className="field__input"
                    type="text"
                    name="recurrence-rule"
                    value={draft.recurrenceCustom}
                    onChange={(e) => setDraft({ ...draft, recurrenceCustom: e.target.value })}
                    placeholder="FREQ=YEARLY;BYMONTH=3;BYMONTHDAY=15"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </Field>
              )}

              {draft.recurrenceMode !== 'none' && draft.recurrenceMode !== 'custom' && (
                <Field
                  label="Ends"
                  hint={
                    draft.recurrenceEnd.kind === 'never'
                      ? 'Repeats indefinitely.'
                      : draft.recurrenceEnd.kind === 'count'
                        ? 'Completes for good after that many occurrences.'
                        : 'The last occurrence is on (or before) this date.'
                  }
                >
                  <select
                    className="field__input field__input--select"
                    name="recurrence-ends"
                    value={draft.recurrenceEnd.kind}
                    onChange={(e) => {
                      const kind = e.target.value;
                      setDraft({
                        ...draft,
                        recurrenceEnd:
                          kind === 'count'
                            ? {
                                kind: 'count',
                                count:
                                  draft.recurrenceEnd.kind === 'count'
                                    ? draft.recurrenceEnd.count
                                    : 10,
                              }
                            : kind === 'until'
                              ? {
                                  kind: 'until',
                                  date:
                                    draft.recurrenceEnd.kind === 'until'
                                      ? draft.recurrenceEnd.date
                                      : draft.dueDate,
                                }
                              : { kind: 'never' },
                      });
                    }}
                  >
                    <option value="never">Never</option>
                    <option value="count">After a number of times</option>
                    <option value="until">On a date</option>
                  </select>
                  {draft.recurrenceEnd.kind === 'count' && (
                    <input
                      className="field__input"
                      type="number"
                      min={1}
                      max={999}
                      name="recurrence-count"
                      value={draft.recurrenceEnd.count}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          recurrenceEnd: {
                            kind: 'count',
                            count: Math.max(1, Number(e.target.value) || 1),
                          },
                        })
                      }
                      aria-label="Number of occurrences"
                    />
                  )}
                  {draft.recurrenceEnd.kind === 'until' && (
                    <input
                      className="field__input"
                      type="date"
                      name="recurrence-until"
                      value={draft.recurrenceEnd.date}
                      onChange={(e) =>
                        e.target.value &&
                        setDraft({
                          ...draft,
                          recurrenceEnd: { kind: 'until', date: e.target.value },
                        })
                      }
                      aria-label="Last occurrence date"
                    />
                  )}
                </Field>
              )}
            </>
          )}

          <Field
            label="Sharing"
            hint={
              draft.visibility === 'shared'
                ? 'Visible to everyone in your household.'
                : 'Private to you. Other members cannot see it.'
            }
          >
            <select
              className="field__input field__input--select"
              name="visibility"
              value={draft.visibility === 'shared' ? 'shared' : 'private'}
              onChange={(e) =>
                setDraft({ ...draft, visibility: e.target.value as ObligationVisibility })
              }
            >
              <option value="private">Private to me</option>
              <option value="shared">Shared with household</option>
            </select>
          </Field>

          <Field
            label="Notes"
            hint={
              draft.kind === 'occasion'
                ? 'Gift ideas, what you did last year, who you contacted — anything that helps next time.'
                : undefined
            }
          >
            <textarea
              className="field__input field__input--textarea"
              name="notes"
              rows={4}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </Field>

          <div className="drawer__meta">
            <span>Created {formatStamp(obligation.createdAt)}</span>
            <span>Last updated {formatStamp(obligation.updatedAt)}</span>
            {obligation.completedAt && (
              <span>Last {obligation.kind === 'occasion' ? 'acknowledged' : 'completed'} {formatStamp(obligation.completedAt)}</span>
            )}
          </div>
        </div>

        <footer className="drawer__footer">
          {confirmingDelete ? (
            <div className="drawer__confirm" role="alertdialog" aria-label="Confirm delete">
              {/* For occasions the title is just the person's name,
                  so "Delete '{title}'?" reads like we're offering to
                  delete the person. Phrase the verb on the entry,
                  show the title as secondary context. */}
              <span className="drawer__confirm-message">
                {draft.kind === 'occasion' ? (
                  <>
                    Delete this {draft.occasionType}?
                    <span className="drawer__confirm-detail">
                      {liveObligationTitle(obligation, people.data)}&rsquo;s{' '}
                      {draft.occasionType} entry will be removed.
                      The person record stays.
                    </span>
                  </>
                ) : (
                  <>
                    Delete this task?
                    <span className="drawer__confirm-detail">
                      &ldquo;{liveObligationTitle(obligation, people.data)}&rdquo; will be removed. This can&rsquo;t be undone.
                    </span>
                  </>
                )}
              </span>
              <div className="drawer__confirm-actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={() => setConfirmingDelete(false)}
                  autoFocus
                >
                  Keep
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={remove}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </button>
              <div className="drawer__footer-actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={save}
                  disabled={saveDisabled}
                >
                  Save
                </button>
              </div>
            </>
          )}
        </footer>
      </aside>

      {viewingPersonId && (
        <PersonCard personId={viewingPersonId} onClose={() => setViewingPersonId(null)} />
      )}
    </>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
